import React, { useEffect, useState, useMemo } from 'react';
import {
  getDeepSearchReports,
  downloadDeepSearchReport,
  getDeepSearchReportById,
  updateDeepSearchReportById,
} from '../api/api';
import { toast } from 'react-hot-toast';
import { useSelector } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import ResearchSkeleton from './ResearchSkeleton';
import MDEditor from '@uiw/react-md-editor';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Eye, Pencil } from 'lucide-react';
import { handleApiError } from '../helpers/handleApiError';

const ResearchView = ({ isDarkMode }) => {
  const user = useSelector((state) => state.auth.user);
  const uid = user?.id;
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState('');
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeReportId, setActiveReportId] = useState(null);
  const [reportTitle, setReportTitle] = useState('');
  const [reportContent, setReportContent] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);

  const PAGE_SIZE = 12;

  const fetchReports = async () => {
    if (!uid) return;

    try {
      setLoading(true);

      const offset = (page - 1) * PAGE_SIZE;

      const data = await getDeepSearchReports(uid, {
        limit: PAGE_SIZE,
        offset,
        preview_chars: 600,
      });

      setReports(data?.items || []);
      setTotal(data?.total || 0);
    } catch (err) {
      setError('Unable to fetch reports');
      handleApiError(err, 'Unable to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [uid, page]);

  useEffect(() => {
    if (!uid) return;
    setPage(1);
  }, [uid]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const visiblePages = useMemo(() => {
    const delta = 2;
    const pages = [];

    for (
      let i = Math.max(1, page - delta);
      i <= Math.min(totalPages, page + delta);
      i++
    ) {
      pages.push(i);
    }

    return pages;
  }, [page, totalPages]);
  const openResearchModal = async (identifier, edit = false) => {
    if (!identifier) {
      toast.error('Invalid research report');
      return;
    }

    // ✅ OPEN MODAL IMMEDIATELY
    setIsEditMode(edit);
    setIsModalOpen(true);
    setModalLoading(true);

    try {
      const res = await getDeepSearchReportById(identifier);

      setActiveReportId(identifier);
      setReportTitle(res?.title || 'Untitled Research');
      setReportContent(res?.content_md || '');
    } catch (err) {
      toast.error('Failed to load research report');
      closeModal();
    } finally {
      setModalLoading(false);
    }
  };

  const handleSaveResearch = async () => {
    if (!activeReportId) return;

    try {
      setIsSaving(true);

      await updateDeepSearchReportById(activeReportId, {
        title: reportTitle,
        content_md: reportContent,
      });

      toast.success('Research report updated');

      closeModal();

      // ✅ refresh list
      await fetchReports();
    } catch {
      toast.error('Failed to update research report');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownload = async (researchId, title) => {
    if (!researchId) {
      toast.error('Invalid report ID');
      return;
    }

    try {
      setDownloading(researchId);
      await downloadDeepSearchReport(researchId, title);
      toast.success(`Downloading “${title || 'Research Report'}”`);
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download report');
    } finally {
      setDownloading('');
    }
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setConfirmSaveOpen(false);
    setIsEditMode(false);
    setActiveReportId(null);
    setReportTitle('');
    setReportContent('');
    setModalLoading(false);
  };

  if (loading) return <ResearchSkeleton isDarkMode={isDarkMode} />;

  if (error)
    return (
      <div
        className={`flex flex-col px-6 py-8 mx-auto w-full h-full overflow-y-auto ${
          isDarkMode ? 'bg-gray-900 text-slate-100' : 'bg-white text-dark'
        }`}
      >
        <div
          className={`flex flex-col items-center justify-center min-h-[80vh] text-center space-y-2 ${
            isDarkMode ? 'text-red-300' : 'text-error'
          }`}
        >
          <span className="text-5xl">⚠️</span>
          <p className="font-medium">{error}</p>
          <p
            className={` text-sm mx-auto ${
              isDarkMode ? 'text-slate-400' : 'text-dark50'
            }`}
          >
            Please try again later.
          </p>
        </div>
      </div>
    );

  const isDataReady =
    !modalLoading && !!activeReportId && typeof reportContent === 'string';

  return (
    <div
      className={`flex flex-col px-6 py-8 mx-auto w-full h-full overflow-y-auto ${
        isDarkMode ? 'bg-gray-900 text-slate-100' : 'bg-white text-dark'
      }`}
    >
      {/* HEADER */}
      <div className="mb-10 text-center">
        <h1
          className={`text-3xl font-bold mb-2 ${
            isDarkMode ? 'text-slate-100' : 'text-dark'
          }`}
        >
          Professional Research Reports
        </h1>

        <p
          className={`text-sm max-w-2xl mx-auto ${
            isDarkMode ? 'text-slate-400' : 'text-dark50'
          }`}
        >
          Comprehensive, AI-assisted research documents crafted to answer your
          legal inquiries — analyzed, summarized, and formatted for clarity.
        </p>
      </div>

      {/* NO REPORTS UI RIGHT HERE */}
      {reports.length === 0 && (
        <div
          className={`flex flex-col items-center justify-center h-[calc(40vh-50px)] w-full text-center space-y-3 ${
            isDarkMode ? 'text-slate-300' : ''
          }`}
        >
          <div className="text-5xl text-brand">🧠</div>

          <p
            className={`text-lg font-medium ${
              isDarkMode ? 'text-slate-100' : 'text-dark'
            }`}
          >
            No professional research reports found.
          </p>

          <p
            className={`text-sm ${
              isDarkMode ? 'text-slate-400' : 'text-dark50'
            }`}
          >
            Start a new deep legal research to see reports here.
          </p>
        </div>
      )}

      {/* GRID */}
      <div className="grid gap-6 w-full max-w-6xl mx-auto grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 auto-rows-fr place-items-stretch">
        {reports.map((report, index) => {
          const status = report?.status || 'unknown';
          const isCompleted = status === 'completed';
          const isRunning = status === 'running';

          const started =
            report?.started_at && !isNaN(new Date(report.started_at))
              ? new Date(report.started_at).toLocaleString()
              : '—';

          const completed = report.completed_at
            ? new Date(report.completed_at).toLocaleString()
            : '—';

          return (
            <div
              key={report?.research_session_id ?? `${page}-${index}`}
              className={`flex flex-col justify-between border rounded-2xl shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 p-6 group w-full ${
                isDarkMode
                  ? 'border-slate-700 bg-slate-800 hover:bg-slate-750'
                  : 'border-brandLighter bg-white'
              }`}
            >
              {/* Header: Title + Status */}
              <div className="flex items-start justify-between mb-3">
                <h3
                  className={`text-lg font-semibold line-clamp-2 transition-colors pr-3 ${
                    isDarkMode
                      ? 'text-slate-100 group-hover:text-indigo-400'
                      : 'text-dark group-hover:text-brand'
                  }`}
                  title={report.title}
                >
                  {report.title || 'Untitled Research'}
                </h3>

                <span
                  className={`px-3 py-1 text-xs font-medium rounded-full shadow-sm whitespace-nowrap ${
                    isCompleted
                      ? isDarkMode
                        ? 'bg-green-900/40 text-green-300 border border-green-700'
                        : 'bg-successBg text-success'
                      : isDarkMode
                      ? 'bg-yellow-900/40 text-yellow-300 border border-yellow-700'
                      : 'bg-warningBg text-warning'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </span>
              </div>

              {/* Content Preview */}
              <div
                className={`flex-grow text-sm mb-4 leading-relaxed prose prose-sm max-w-none font-normal overflow-hidden line-clamp-5 ${
                  isDarkMode
                    ? 'text-slate-300 prose-headings:text-slate-100 prose-strong:text-slate-200'
                    : 'text-dark50 prose-headings:text-dark prose-strong:text-dark'
                }`}
              >
                {status === 'running' ? (
                  <div
                    className={`h-20 rounded-md mb-4 ${
                      isDarkMode
                        ? 'shimmer-dark bg-slate-700'
                        : 'shimmer bg-gray-200'
                    }`}
                  />
                ) : (
                  <div className="preview-content">
                    <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                      {report.content_preview || ''}
                    </ReactMarkdown>
                  </div>
                )}
              </div>

              {/* Metadata */}
              <div
                className={`text-xs mb-5 space-y-1 ${
                  isDarkMode ? 'text-slate-400' : 'text-dark50'
                }`}
              >
                <p>🕓 Started: {started}</p>
                <p>✅ Completed: {completed}</p>
              </div>

              <div className="flex gap-2 mb-4">
                {/* View */}
                <button
                  onClick={() => {
                    if (isRunning) return;
                    openResearchModal(report.research_session_id, false);
                  }}
                  disabled={isRunning}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition
    ${
      isRunning
        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
        : isDarkMode
        ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    }
  `}
                >
                  <Eye size={16} />
                  View Report
                </button>

                {/* Edit */}
                <button
                  onClick={() => {
                    if (!report?.research_session_id) {
                      toast.error('This report is not ready yet');
                      return;
                    }
                    openResearchModal(report.research_session_id, true);
                  }}
                  disabled={!isCompleted}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition
    ${
      isCompleted
        ? 'bg-indigo-600 text-white hover:bg-indigo-700'
        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
    }
  `}
                >
                  <Pencil size={16} />
                  Edit Report
                </button>
              </div>

              {/* Download Button */}
              {isCompleted ? (
                <button
                  onClick={() =>
                    handleDownload(report?.research_session_id, report.title)
                  }
                  disabled={downloading === report?.research_session_id}
                  className={`mt-auto w-full py-2.5 rounded-lg font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
                    downloading === report?.research_session_id
                      ? isDarkMode
                        ? 'bg-indigo-700 text-slate-300 cursor-not-allowed'
                        : 'bg-brandLight text-white cursor-not-allowed'
                      : 'bg-gradient-to-r from-indigo-500 to-indigo-700 text-white hover:shadow-md hover:scale-[1.02]'
                  }`}
                >
                  {downloading === report?.research_session_id ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        />
                      </svg>
                      <span>Preparing download…</span>
                    </>
                  ) : (
                    <>
                      <span>⬇️</span>
                      <span>Download Report</span>
                    </>
                  )}
                </button>
              ) : (
                <button
                  disabled
                  className={`mt-auto w-full py-2.5 rounded-lg font-medium text-sm opacity-50 cursor-not-allowed ${
                    isDarkMode
                      ? 'bg-slate-700 text-slate-400'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  Report still processing…
                </button>
              )}
            </div>
          );
        })}
      </div>
      {totalPages > 1 && (
        <div className="mt-12 flex justify-center">
          <div
            role="navigation"
            aria-label="Pagination"
            className={`flex items-center gap-2 px-4 py-3 rounded-xl shadow-sm ${
              isDarkMode
                ? 'bg-slate-800 border border-slate-700'
                : 'bg-white border border-gray-200'
            }`}
          >
            {/* Previous */}
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                page === 1 || loading
                  ? 'opacity-40 cursor-not-allowed'
                  : isDarkMode
                  ? 'text-slate-300 hover:bg-slate-700'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              ←
            </button>

            {/* First page shortcut */}
            {page > 3 && (
              <>
                <button
                  onClick={() => setPage(1)}
                  className={`px-3 py-2 rounded-lg text-sm ${
                    isDarkMode
                      ? 'text-slate-300 hover:bg-slate-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  1
                </button>
                <span className="px-1 text-sm opacity-50">…</span>
              </>
            )}

            {/* Page numbers */}
            {visiblePages.map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                disabled={loading}
                aria-current={p === page ? 'page' : undefined}
                className={`px-3 py-2 rounded-lg text-sm font-semibold transition ${
                  p === page
                    ? 'bg-indigo-600 text-white shadow'
                    : isDarkMode
                    ? 'text-slate-300 hover:bg-slate-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {p}
              </button>
            ))}

            {/* Last page shortcut */}
            {page < totalPages - 2 && (
              <>
                <span className="px-1 text-sm opacity-50">…</span>
                <button
                  onClick={() => setPage(totalPages)}
                  className={`px-3 py-2 rounded-lg text-sm ${
                    isDarkMode
                      ? 'text-slate-300 hover:bg-slate-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {totalPages}
                </button>
              </>
            )}

            {/* Next */}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                page === totalPages || loading
                  ? 'opacity-40 cursor-not-allowed'
                  : isDarkMode
                  ? 'text-slate-300 hover:bg-slate-700'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              →
            </button>
          </div>
        </div>
      )}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4"
            onClick={() => {
              if (!isEditMode && isDataReady) closeModal();
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-6xl h-[90vh] flex flex-col rounded-xl overflow-hidden shadow-xl
    ${
      isDarkMode
        ? 'bg-gray-900 border-gray-700 text-gray-100'
        : 'bg-white border-gray-200 text-gray-900'
    }
  `}
            >
              {/* Header */}
              <div
                className={`flex items-start justify-between gap-4 px-6 py-4 border-b
    ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}
  `}
              >
                {/* Title */}
                <div className="flex-1 min-w-0">
                  <h2
                    className={`text-xl font-semibold leading-snug
        ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}
      `}
                  >
                    {reportTitle}
                  </h2>

                  <p
                    className={`mt-1 text-sm flex items-center gap-2
        ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
      `}
                  >
                    {isEditMode && (
                      <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                    )}
                    {isEditMode ? 'Edit mode — changes will be saved' : ''}
                  </p>
                </div>

                {/* Close */}
                <button
                  onClick={() => closeModal()}
                  aria-label="Close"
                  className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition
      ${
        isDarkMode
          ? 'text-gray-400 hover:text-white hover:bg-gray-800'
          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
      }
    `}
                >
                  <X size={18} strokeWidth={1.75} />
                </button>
              </div>

              {/* Content */}
              <div className="px-8 py-6 flex-1 overflow-y-auto">
                {modalLoading ? (
                  <div className="flex flex-1 items-center justify-center">
                    <span className="animate-spin h-10 w-10 rounded-full border-2 border-gray-400 border-t-transparent opacity-80" />
                  </div>
                ) : isEditMode ? (
                  <div
                    data-color-mode={isDarkMode ? 'dark' : 'light'}
                    className="h-full flex flex-col"
                  >
                    <MDEditor
                      value={reportContent}
                      onChange={setReportContent}
                      height="100%"
                    />
                  </div>
                ) : (
                  <div
                    data-color-mode={isDarkMode ? 'dark' : 'light'}
                    className={`prose max-w-none ${
                      isDarkMode ? 'prose-invert' : ''
                    }`}
                  >
                    <MDEditor.Markdown source={reportContent} />
                  </div>
                )}
              </div>

              {/* Footer */}
              {isEditMode && (
                <div
                  className={`sticky bottom-0 z-10 border-t px-6 py-4 flex justify-end gap-3 backdrop-blur-md
      ${
        isDarkMode
          ? 'bg-gray-900/80 border-gray-700'
          : 'bg-white/80 border-gray-200'
      }
    `}
                >
                  <button
                    onClick={closeModal}
                    disabled={!isDataReady}
                    className={`px-4 py-2 text-sm rounded transition
    ${
      !isDataReady
        ? 'text-gray-400 cursor-not-allowed'
        : isDarkMode
        ? 'text-gray-300 hover:text-white'
        : 'text-gray-500 hover:text-gray-700'
    }
  `}
                  >
                    Cancel
                  </button>

                  <button
                    onClick={() => setConfirmSaveOpen(true)}
                    disabled={!isDataReady || isSaving}
                    className={`px-6 py-2 rounded-md font-semibold transition
    ${
      !isDataReady || isSaving
        ? 'bg-gray-400 cursor-not-allowed text-white'
        : 'bg-indigo-600 hover:bg-indigo-700 text-white'
    }
  `}
                  >
                    {isSaving ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {confirmSaveOpen && (
          <motion.div
            className="fixed inset-0 z-[10000] bg-black/70 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className={`w-full max-w-md rounded-xl shadow-xl p-6
          ${isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'}
        `}
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
            >
              <h3 className="text-lg font-semibold mb-2">
                Confirm save changes
              </h3>

              <p className="text-sm opacity-80 mb-6">
                You’re about to overwrite this research report.
                <br />
                This action cannot be undone.
              </p>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setConfirmSaveOpen(false)}
                  className="px-4 py-2 rounded border"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!isDataReady) return;
                    setConfirmSaveOpen(false);
                    await handleSaveResearch();
                  }}
                  disabled={!isDataReady || isSaving}
                  className={`px-4 py-2 rounded font-semibold
    ${
      !isDataReady || isSaving
        ? 'bg-gray-400 cursor-not-allowed text-white'
        : 'bg-indigo-600 hover:bg-indigo-700 text-white'
    }
  `}
                >
                  Yes, save and update
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ResearchView;
